import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  CalendarIcon, 
  SparklesIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const UsagePage = () => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30d'); // 7d, 30d, 90d, custom
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (timeframe === 'custom') {
      setShowDatePicker(true);
    } else {
      fetchUsageData();
    }
  }, [timeframe]);

  const handleCustomDateRange = async () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.getUsageStatistics(`${dateRange.start}|${dateRange.end}`);
      setUsageData(response.data.usage);
      setShowDatePicker(false);
    } catch (error) {
      console.error('Error fetching custom usage data:', error);
      toast.error('Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const response = await api.getUsageStatistics(timeframe);
      setUsageData(response.data.usage);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      toast.error('Failed to load usage statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600 bg-red-50';
    if (percentage >= 75) return 'text-orange-600 bg-orange-50';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const currentPeriod = usageData?.current_period || {};
  const subscription = usageData?.subscription || {};
  const dailyBreakdown = usageData?.daily_breakdown || [];
  const recentActivity = usageData?.recent_activity || [];

  // Pagination for recent activity
  const totalPages = Math.ceil(recentActivity.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedActivity = recentActivity.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  console.log(usageData);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usage & Analytics</h1>
          <p className="text-gray-600 mt-1">Monitor your character usage and subscription limits</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="custom">Custom Range</option>
          </select>
          <button
            onClick={fetchUsageData}
            className="btn-secondary btn-sm"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Date Range Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-end pt-16 pr-4 z-50">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Custom Date Range</h3>
              <button
                onClick={() => {
                  setShowDatePicker(false);
                  setTimeframe('30d');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleCustomDateRange}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  Apply
                </button>
                <button
                  onClick={() => {
                    setShowDatePicker(false);
                    setTimeframe('30d');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Characters Used */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Characters Used</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(currentPeriod.characters_used || 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                of {currentPeriod.characters_limit === 'unlimited' 
                  ? 'unlimited' 
                  : formatNumber(parseInt(currentPeriod.characters_limit || 0))}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${getUsageColor(currentPeriod.usage_percentage || 0)}`}>
              <ChartBarIcon className="h-6 w-6" />
            </div>
          </div>
          
          {/* Usage Bar */}
          <div className="mt-4">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  (currentPeriod.usage_percentage || 0) >= 90 ? 'bg-red-500' :
                  (currentPeriod.usage_percentage || 0) >= 75 ? 'bg-orange-500' :
                  (currentPeriod.usage_percentage || 0) >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(currentPeriod.usage_percentage || 0, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>{(currentPeriod.usage_percentage || 0).toFixed(1)}%</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        {/* WhatsApp Agents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">WhatsApp Agents</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {currentPeriod.whatsapp_agents_used || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                of {currentPeriod.whatsapp_agents_limit === 'unlimited' 
                  ? 'unlimited' 
                  : currentPeriod.whatsapp_agents_limit || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <UsersIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Messages Processed */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Messages Processed</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatNumber(currentPeriod.messages_processed || 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">this month</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Current Package */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Current Package</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {subscription.package?.name || 'Trial'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {subscription.subscription_status === 'trial' ? 'Free Trial' : 'Active Subscription'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
              <SparklesIcon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Usage Alerts */}
      {currentPeriod.usage_percentage >= 75 && (
        <div className={`rounded-lg border p-4 ${
          currentPeriod.usage_percentage >= 90
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
        }`}>
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            <div>
              <div className="font-semibold">
                {currentPeriod.usage_percentage >= 90 ? 'Critical Usage Alert' : 'High Usage Warning'}
              </div>
              <div className="text-sm mt-1">
                You've used {currentPeriod.usage_percentage.toFixed(1)}% of your monthly character limit. 
                Consider upgrading your plan to avoid service interruption.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Usage Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Daily Character Usage
          </h2>
          <p className="text-sm text-gray-600 mt-1">Character usage breakdown for the last 30 days</p>
        </div>
        <div className="p-6">
          {dailyBreakdown && dailyBreakdown.length > 0 ? (
            <div className="space-y-4">
              {/* Chart Header */}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Date</span>
                <span>Characters Used</span>
              </div>
              
              {/* Chart Bars */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {dailyBreakdown.slice(0, 30).map((day, index) => {
                  const maxUsage = Math.max(...dailyBreakdown.map(d => d.characters));
                  const percentage = maxUsage > 0 ? (day.characters / maxUsage) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div className="text-sm text-gray-600 w-24">
                        {new Date(day.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="bg-gray-200 rounded-full h-3 relative">
                          <div
                            className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900 w-20 text-right">
                        {formatNumber(day.characters)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No usage data available</p>
              <p className="text-sm text-gray-400 mt-1">Start using your WhatsApp agents to see analytics here</p>
            </div>
          )}
        </div>
      </div>

      {/* Package Limits Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Package Limits</h2>
          <p className="text-sm text-gray-600 mt-1">Your current subscription limits and usage</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Characters Limit */}
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {currentPeriod.characters_limit === 'unlimited' 
                  ? '∞' 
                  : formatNumber(parseInt(currentPeriod.characters_limit || 0))}
              </div>
              <div className="text-sm font-medium text-blue-800">Characters/Month</div>
              <div className="text-xs text-blue-600 mt-1">
                {formatNumber(currentPeriod.characters_used || 0)} used
              </div>
            </div>

            {/* WhatsApp Agents Limit */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {currentPeriod.whatsapp_agents_limit === 'unlimited' 
                  ? '∞' 
                  : currentPeriod.whatsapp_agents_limit || 0}
              </div>
              <div className="text-sm font-medium text-green-800">WhatsApp Agents</div>
              <div className="text-xs text-green-600 mt-1">
                {currentPeriod.whatsapp_agents_used || 0} active
              </div>
            </div>

            {/* AI Agents Limit */}
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600 mb-1">
                {currentPeriod.ai_agents_limit === 'unlimited' 
                  ? '∞' 
                  : currentPeriod.ai_agents_limit || 0}
              </div>
              <div className="text-sm font-medium text-purple-800">AI Agents</div>
              <div className="text-xs text-purple-600 mt-1">
                {currentPeriod.ai_agents_used || 0} created
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Usage Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Usage Trends
          </h2>
          <p className="text-sm text-gray-600 mt-1">Historical character usage patterns</p>
        </div>
        <div className="p-6">
          {dailyBreakdown && dailyBreakdown.length > 0 ? (
            <div className="grid grid-cols-7 gap-2">
              {/* Week Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              
              {/* Usage Heatmap */}
              {Array.from({ length: 28 }, (_, index) => {
                const date = new Date();
                date.setDate(date.getDate() - (27 - index));
                const dateStr = date.toISOString().split('T')[0];
                const dayData = dailyBreakdown.find(d => d.date === dateStr);
                const usage = dayData?.characters || 0;
                const maxUsage = Math.max(...dailyBreakdown.map(d => d.characters));
                const intensity = maxUsage > 0 ? Math.ceil((usage / maxUsage) * 4) : 0;
                
                return (
                  <div
                    key={index}
                    className={`h-8 rounded text-xs flex items-center justify-center border transition-all hover:scale-110 cursor-help ${
                      intensity === 0 ? 'bg-gray-100 border-gray-200' :
                      intensity === 1 ? 'bg-blue-100 border-blue-200' :
                      intensity === 2 ? 'bg-blue-300 border-blue-400' :
                      intensity === 3 ? 'bg-blue-500 border-blue-600 text-white' :
                      'bg-blue-700 border-blue-800 text-white'
                    }`}
                    title={`${date.toLocaleDateString()}: ${usage.toLocaleString()} characters`}
                  >
                    {usage > 0 && <span className="text-xs">{formatNumber(usage)}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No usage trends available yet</p>
              <p className="text-sm text-gray-400 mt-1">Usage data will appear here as you use the service</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity - COMMENTED OUT */}
      {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <p className="text-sm text-gray-600 mt-1">Latest character usage and system activity</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {paginatedActivity && paginatedActivity.length > 0 ? (
              paginatedActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        AI response via {activity.agent_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                      {activity.preview && (
                        <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                          "{activity.preview}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-600">
                    +{activity.characters.toLocaleString()} characters
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No recent activity</p>
                <p className="text-xs text-gray-400 mt-1">AI responses will appear here</p>
              </div>
            )}

            {recentActivity.length > itemsPerPage && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(startIndex + itemsPerPage, recentActivity.length)}
                  </span>{' '}
                  of <span className="font-medium">{recentActivity.length}</span> results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <span className="inline-flex items-center px-2 py-1 text-sm text-gray-500">
                        ...
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default UsagePage;